import useCurrentMerchant from 'hooks/useCurrentMerchant'
import moment from 'moment-timezone'
import useMonthlyOrders from './useMonthlyOrders'

// A brand must be live at least this long before its 30-day ROI projection is
// trustworthy enough to show (D2/D10). Null live_at ⇒ not yet live ⇒ too new.
// The "30 days post-live" and "no data ⇒ hide the whole component" gates come
// from FR-8's decision story (comments 1063/1192, Aaron), not the FR-8 v9.8
// requirement BODY — which dropped both lines. Keep them: they're settled intent.
export const MIN_DAYS_LIVE = 30

/**
 * Unified "show RoiSection" gate (D2/D10/D14/D15, the C2 lock): tenure ≥30d
 * live AND a computable, non-zero 👎-based upside. `negativeRatingCount ===
 * null` (BE field absent/unavailable) and `=== 0` (real zero) both hide —
 * there is exactly one predicate, and `null` never gets a "render anyway"
 * fallback here (that fallback belongs only to RoiSection's own
 * defense-in-depth, unreachable via this gated page).
 *
 *   context: hides during `isLoading` on purpose — the ROI band is a
 *   below-the-fold reveal, so hiding-until-committable avoids a
 *   spinner-then-vanish flash. RTK Query dedupes this hook's
 *   `useMonthlyOrders()` call with RoiSection's own, so no second fetch.
 */
const useShowRoiSection = (): boolean => {
  const merchant = useCurrentMerchant()
  const { isLoading, monthlyOrders, negativeRatingCount } = useMonthlyOrders()

  const liveAt = merchant?.live_at
  const tenureOk =
    !!liveAt && moment().diff(moment(liveAt), 'days') >= MIN_DAYS_LIVE

  const hasComputableUpside =
    !isLoading &&
    monthlyOrders != null &&
    monthlyOrders > 0 &&
    negativeRatingCount != null &&
    negativeRatingCount > 0

  return tenureOk && hasComputableUpside
}

export default useShowRoiSection
